// src/services/TenantConfigService.js

import { db } from '../firebase/config.js'; // CORRECT import path for your project
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  query, 
  where, 
  getDocs,
  updateDoc,
  deleteDoc,
  serverTimestamp 
} from 'firebase/firestore';

class TenantConfigService {
  static cache = new Map();
  static cacheTimeout = 60 * 60 * 1000; // 1 hour in milliseconds

  /**
   * Get configuration for a tenant
   */
static async getConfig(tenantId, configKey, defaultValue = null) {
  const cacheKey = `${tenantId}_${configKey}`;
  
  // Check cache first
  if (this.cache.has(cacheKey)) {
    const cached = this.cache.get(cacheKey);
    if (Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
  }

  try {
    // NEW: Use subcollection structure
    const docRef = doc(db, 'tenant_configs', tenantId, 'configs', configKey);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const configValue = docSnap.data();
      
      // Cache the result
      this.cache.set(cacheKey, {
        data: configValue,
        timestamp: Date.now()
      });
      
      return configValue;
    }
    
    return defaultValue;
  } catch (error) {
    console.error('Error fetching config:', error);
    return defaultValue;
  }
}

  /**
   * Get all periods for a tenant
   */
  static async getPeriods(tenantId) {
    const periodsConfig = await this.getConfig(tenantId, 'periods', {
      periods: [
        { id: '1', name: 'Period 1', start_time: '', end_time: '' },
        { id: '2', name: 'Period 2', start_time: '', end_time: '' },
        { id: '3', name: 'Period 3', start_time: '', end_time: '' },
        { id: '4', name: 'Period 4', start_time: '', end_time: '' },
        { id: '5', name: 'Period 5', start_time: '', end_time: '' },
        { id: '6', name: 'Period 6', start_time: '', end_time: '' },
        { id: '7', name: 'Period 7', start_time: '', end_time: '' },
        { id: '8', name: 'Period 8', start_time: '', end_time: '' }
      ]
    });
    
    return periodsConfig.periods || [];
  }

  /**
   * Get custom fields for periods
   */
  static async getPeriodCustomFields(tenantId) {
    const periodsConfig = await this.getConfig(tenantId, 'periods', { customFields: [] });
    return periodsConfig.customFields || [];
  }

  /**
   * Get academic departments
   */
  static async getAcademicDepartments(tenantId) {
    const config = await this.getConfig(tenantId, 'academic_departments', {
      departments: ['English', 'Math', 'Science', 'History', 'World Languages', 'PE', 'Arts'],
      customDepartments: []
    });
    
    return [...(config.departments || []), ...(config.customDepartments || [])];
  }

  /**
   * Get school departments
   */
  static async getSchoolDepartments(tenantId) {
    const config = await this.getConfig(tenantId, 'school_departments', {
      departments: ['HR', 'Administration', 'Operations'],
      customDepartments: []
    });
    
    return [...(config.departments || []), ...(config.customDepartments || [])];
  }

  /**
   * Get enabled modules - UPDATED: Now includes volunteer management
   */
  static async getEnabledModules(tenantId) {
    const config = await this.getConfig(tenantId, 'modules', {
      enabledModules: ['Absence Management', 'Substitute Management'] // Default old format
    });
    
    // Handle both formats for backward compatibility
    if (config.enabledModules) {
      if (Array.isArray(config.enabledModules)) {
        // OLD FORMAT: Return as-is
        return config.enabledModules;
      } else if (typeof config.enabledModules === 'object') {
        // NEW FORMAT: Convert object to array for backward compatibility
        return Object.entries(config.enabledModules)
          .filter(([key, value]) => value)
          .map(([key, value]) => {
            switch(key) {
              case 'absenceManagement': return 'Absence Management';
              case 'substituteManagement': return 'Substitute Management';
              case 'subPayManagement': return 'Sub Pay Management';
              case 'volunteerManagement': return 'Volunteer Management';
              default: return key;
            }
          });
      }
    }
    
    return ['Absence Management', 'Substitute Management']; // Default fallback
  }

  /**
   * Get enabled modules in object format - UPDATED: Now includes volunteer management
   */
  static async getEnabledModulesObject(tenantId) {
    const config = await this.getConfig(tenantId, 'modules', {
      enabledModules: {
        absenceManagement: true,
        substituteManagement: true,
        subPayManagement: false,
        volunteerManagement: false
      }
    });
    
    // Handle both formats
    if (config.enabledModules) {
      if (typeof config.enabledModules === 'object' && !Array.isArray(config.enabledModules)) {
        // NEW FORMAT: Return as-is with volunteer management default
        return {
          absenceManagement: config.enabledModules.absenceManagement !== false,
          substituteManagement: config.enabledModules.substituteManagement !== false,
          subPayManagement: config.enabledModules.subPayManagement || false,
          volunteerManagement: config.enabledModules.volunteerManagement || false
        };
      } else if (Array.isArray(config.enabledModules)) {
        // OLD FORMAT: Convert array to object
        return {
          absenceManagement: config.enabledModules.includes('Absence Management'),
          substituteManagement: config.enabledModules.includes('Substitute Management'),
          subPayManagement: config.enabledModules.includes('Sub Pay Management') || config.subPayEnabled,
          volunteerManagement: config.enabledModules.includes('Volunteer Management') || false
        };
      }
    }
    
    // Default fallback
    return {
      absenceManagement: true,
      substituteManagement: true,
      subPayManagement: config.subPayEnabled || false,
      volunteerManagement: false
    };
  }

  /**
   * Check if volunteer management is enabled - NEW
   */
  static async isVolunteerManagementEnabled(tenantId) {
    const config = await this.getConfig(tenantId, 'modules', { volunteerManagementEnabled: false });
    
    // Check both old and new formats
    if (config.volunteerManagementEnabled) return true;
    if (config.enabledModules) {
      if (Array.isArray(config.enabledModules)) {
        return config.enabledModules.includes('Volunteer Management');
      } else if (typeof config.enabledModules === 'object') {
        return config.enabledModules.volunteerManagement === true;
      }
    }
    
    return false;
  }

  /**
   * Get volunteer management configuration - NEW
   */
  static async getVolunteerManagementConfig(tenantId) {
    return await this.getConfig(tenantId, 'volunteer_management', {
      publicRegistration: true,
      autoApproval: false,
      backgroundCheckRequired: true,
      registrationFormFields: [
        { id: 'firstName', name: 'First Name', type: 'text', required: true },
        { id: 'lastName', name: 'Last Name', type: 'text', required: true },
        { id: 'email', name: 'Email Address', type: 'email', required: true },
        { id: 'phone', name: 'Phone Number', type: 'tel', required: true },
        { id: 'address', name: 'Address', type: 'textarea', required: false },
        { id: 'skills', name: 'Skills & Interests', type: 'textarea', required: false },
        { id: 'availability', name: 'Availability', type: 'textarea', required: false },
        { id: 'emergencyContact', name: 'Emergency Contact', type: 'text', required: true },
        { id: 'emergencyPhone', name: 'Emergency Contact Phone', type: 'tel', required: true }
      ],
      emailNotifications: {
        newRegistration: {
          enabled: true,
          recipients: [],
          subject: 'New Volunteer Registration - {name}',
          timing: 'immediate'
        },
        volunteerApproved: {
          enabled: true,
          subject: 'Welcome to {organizationName} - Volunteer Account Approved',
          timing: 'immediate'
        },
        eventAssignment: {
          enabled: true,
          subject: 'Volunteer Assignment - {eventName} on {date}',
          timing: 'immediate'
        },
        eventReminder: {
          enabled: true,
          subject: 'Reminder: {eventName} Tomorrow',
          timing: 'day_before'
        }
      },
      antiSpam: {
        enableRecaptcha: true,
        rateLimitPerIP: 3, // Max registrations per IP per day
        enableHoneypot: true,
        requireEmailVerification: true
      },
      customSkills: [
        'Youth Ministry',
        'Children\'s Ministry',
        'Music/Worship',
        'Audio/Visual',
        'Hospitality',
        'Food Service',
        'Administrative',
        'Maintenance',
        'Security',
        'Transportation',
        'Event Setup',
        'Cleaning',
        'Greeting/Ushering'
      ]
    });
  }

  /**
   * Check if sub pay is enabled
   */
  static async isSubPayEnabled(tenantId) {
    const config = await this.getConfig(tenantId, 'modules', { subPayEnabled: false });
    
    // Check both old and new formats
    if (config.subPayEnabled) return true;
    if (config.enabledModules) {
      if (Array.isArray(config.enabledModules)) {
        return config.enabledModules.includes('Sub Pay Management');
      } else if (typeof config.enabledModules === 'object') {
        return config.enabledModules.subPayManagement === true;
      }
    }
    
    return false;
  }

  /**
   * Get sub pay configuration
   */
  static async getSubPayConfig(tenantId) {
    return await this.getConfig(tenantId, 'sub_pay', {
      types: ['Internal', 'External'],
      defaultType: 'Internal',
      internalRate: 25.00,
      externalRate: 30.00
    });
  }

  /**
   * Get email system configuration
   */
  static async getEmailSystemConfig(tenantId) {
    return await this.getConfig(tenantId, 'email_system', {
      autoSendDepartment: true,
      autoSendFaculty: false,
      departmentTiming: {
        day: 'day_before',
        time: '16:00'
      },
      facultyTiming: {
        day: 'day_of',
        time: '06:00'
      }
    });
  }

  /**
   * Update configuration
   */
static async updateConfig(tenantId, configKey, configValue) {
  try {
    // NEW: Use subcollection structure
    const docRef = doc(db, 'tenant_configs', tenantId, 'configs', configKey);
    await setDoc(docRef, {
      ...configValue,
      tenantId,
      updatedAt: serverTimestamp()
    }, { merge: true });

    // Clear cache
    const cacheKey = `${tenantId}_${configKey}`;
    this.cache.delete(cacheKey);
    
    return true;
  } catch (error) {
    console.error('Error updating config:', error);
    return false;
  }
}

  /**
   * Get all configurations for a tenant (for React components) - UPDATED: Includes volunteer management
   */
  static async getAllConfigsForJS(tenantId) {
    try {
      const [
        periods,
        customFields,
        academicDepartments,
        schoolDepartments,
        enabledModules,
        subPayEnabled,
        subPayConfig,
        emailSystem,
        volunteerManagementEnabled,
        volunteerManagementConfig
      ] = await Promise.all([
        this.getPeriods(tenantId),
        this.getPeriodCustomFields(tenantId),
        this.getAcademicDepartments(tenantId),
        this.getSchoolDepartments(tenantId),
        this.getEnabledModules(tenantId), // Returns array format for backward compatibility
        this.isSubPayEnabled(tenantId),
        this.getSubPayConfig(tenantId),
        this.getEmailSystemConfig(tenantId),
        this.isVolunteerManagementEnabled(tenantId),
        this.getVolunteerManagementConfig(tenantId)
      ]);

      return {
        periods,
        periodCustomFields: customFields,
        academicDepartments,
        schoolDepartments,
        enabledModules, // Array format for backward compatibility
        subPayEnabled,
        subPayConfig,
        emailSystem,
        volunteerManagementEnabled,
        volunteerManagementConfig
      };
    } catch (error) {
      console.error('Error getting all configs:', error);
      return {
        periods: [],
        periodCustomFields: [],
        academicDepartments: ['English', 'Math', 'Science', 'History', 'World Languages', 'PE', 'Arts'],
        schoolDepartments: ['HR', 'Administration', 'Operations'],
        enabledModules: ['Absence Management', 'Substitute Management'],
        subPayEnabled: false,
        subPayConfig: {
          types: ['Internal', 'External'],
          defaultType: 'Internal',
          internalRate: 25.00,
          externalRate: 30.00
        },
        emailSystem: {
          autoSendDepartment: true,
          autoSendFaculty: false,
          departmentTiming: { day: 'day_before', time: '16:00' },
          facultyTiming: { day: 'day_of', time: '06:00' }
        },
        volunteerManagementEnabled: false,
        volunteerManagementConfig: {}
      };
    }
  }

  /**
   * Clear cache for a tenant
   */
  static clearTenantCache(tenantId) {
    const configKeys = [
      'periods', 'schedule', 'academic_departments', 'school_departments',
      'modules', 'sub_pay', 'email_system', 'schedule_types', 'calendar_assignments', 
      'school_holidays', 'volunteer_management'
    ];
    
    configKeys.forEach(key => {
      const cacheKey = `${tenantId}_${key}`;
      this.cache.delete(cacheKey);
    });
  }

  /**
   * Initialize default configurations for a new tenant - UPDATED: Includes volunteer management
   */
  static async initializeDefaultConfigs(tenantId) {
    const defaultConfigs = {
      periods: {
        periods: [
          { id: '1', name: 'Period 1', start_time: '', end_time: '' },
          { id: '2', name: 'Period 2', start_time: '', end_time: '' },
          { id: '3', name: 'Period 3', start_time: '', end_time: '' },
          { id: '4', name: 'Period 4', start_time: '', end_time: '' },
          { id: '5', name: 'Period 5', start_time: '', end_time: '' },
          { id: '6', name: 'Period 6', start_time: '', end_time: '' },
          { id: '7', name: 'Period 7', start_time: '', end_time: '' },
          { id: '8', name: 'Period 8', start_time: '', end_time: '' }
        ],
        customFields: []
      },
      schedule_types: {
        scheduleTypes: [
          {
            id: 'regular',
            name: 'Regular Day',
            description: 'Standard 8-period day',
            periods: [
              { id: '1', name: 'Period 1', start_time: '08:00', end_time: '08:50' },
              { id: '2', name: 'Period 2', start_time: '08:55', end_time: '09:45' },
              { id: '3', name: 'Period 3', start_time: '09:50', end_time: '10:40' },
              { id: '4', name: 'Period 4', start_time: '10:45', end_time: '11:35' },
              { id: '5', name: 'Period 5', start_time: '11:40', end_time: '12:30' },
              { id: '6', name: 'Period 6', start_time: '12:35', end_time: '13:25' },
              { id: '7', name: 'Period 7', start_time: '13:30', end_time: '14:20' },
              { id: '8', name: 'Period 8', start_time: '14:25', end_time: '15:15' }
            ],
            isDefault: true
          }
        ],
        customFields: []
      },
      calendar_assignments: {
        patterns: [],
        specificDates: []
      },
      school_holidays: {
        holidays: []
      },
      academic_departments: {
        departments: ['English', 'Math', 'Science', 'History', 'World Languages', 'PE', 'Arts'],
        customDepartments: []
      },
      school_departments: {
        departments: ['HR', 'Administration', 'Operations'],
        customDepartments: []
      },
      modules: {
        // NEW: Save both formats for compatibility, now includes volunteer management
        enabledModules: {
          absenceManagement: true,
          substituteManagement: true,
          subPayManagement: false,
          volunteerManagement: false
        },
        enabledModulesArray: ['Absence Management', 'Substitute Management'], // For backward compatibility
        subPayEnabled: false,
        volunteerManagementEnabled: false
      },
      sub_pay: {
        types: ['Internal', 'External'],
        defaultType: 'Internal',
        internalRate: 25.00,
        externalRate: 30.00
      },
      email_system: {
        autoSendDepartment: true,
        autoSendFaculty: false,
        departmentTiming: { day: 'day_before', time: '16:00' },
        facultyTiming: { day: 'day_of', time: '06:00' }
      },
      volunteer_management: {
        publicRegistration: true,
        autoApproval: false,
        backgroundCheckRequired: true,
        registrationFormFields: [
          { id: 'firstName', name: 'First Name', type: 'text', required: true },
          { id: 'lastName', name: 'Last Name', type: 'text', required: true },
          { id: 'email', name: 'Email Address', type: 'email', required: true },
          { id: 'phone', name: 'Phone Number', type: 'tel', required: true },
          { id: 'address', name: 'Address', type: 'textarea', required: false },
          { id: 'skills', name: 'Skills & Interests', type: 'textarea', required: false },
          { id: 'availability', name: 'Availability', type: 'textarea', required: false },
          { id: 'emergencyContact', name: 'Emergency Contact', type: 'text', required: true },
          { id: 'emergencyPhone', name: 'Emergency Contact Phone', type: 'tel', required: true }
        ],
        emailNotifications: {
          newRegistration: {
            enabled: true,
            recipients: [],
            subject: 'New Volunteer Registration - {name}',
            timing: 'immediate'
          },
          volunteerApproved: {
            enabled: true,
            subject: 'Welcome to {organizationName} - Volunteer Account Approved',
            timing: 'immediate'
          },
          eventAssignment: {
            enabled: true,
            subject: 'Volunteer Assignment - {eventName} on {date}',
            timing: 'immediate'
          },
          eventReminder: {
            enabled: true,
            subject: 'Reminder: {eventName} Tomorrow',
            timing: 'day_before'
          }
        },
        antiSpam: {
          enableRecaptcha: true,
          rateLimitPerIP: 3,
          enableHoneypot: true,
          requireEmailVerification: true
        },
        customSkills: [
          'Youth Ministry',
          'Children\'s Ministry',
          'Music/Worship',
          'Audio/Visual',
          'Hospitality',
          'Food Service',
          'Administrative',
          'Maintenance',
          'Security',
          'Transportation',
          'Event Setup',
          'Cleaning',
          'Greeting/Ushering'
        ]
      },
      system_config: {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        dateFormat: 'MM/DD/YYYY',
        timeFormat: '12h'
      }
    };

    try {
      const promises = Object.entries(defaultConfigs).map(([configKey, configValue]) =>
        this.updateConfig(tenantId, configKey, configValue)
      );

      await Promise.all(promises);
      console.log(`Default configurations initialized for tenant: ${tenantId}`);
      return true;
    } catch (error) {
      console.error('Error initializing default configs:', error);
      return false;
    }
  }
}

export default TenantConfigService;